import ServiceUnavailableAlert from 'components/core/ServiceUnavailableAlert';
import { parseISO } from 'date-fns';
import { useAgendaActions } from 'hooks/useAgendaActions';
import { useFormAgenda } from 'hooks/useFormAgenda';
import { usePainel } from 'hooks/usePainel'; // Hook do Painel do Médico
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Offcanvas, Row, Spinner } from 'react-bootstrap';
import { BsList } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { selectDoctor, selectFunc } from 'redux/features/auth/authSlice';
import { buscaAgendaDoMedico } from 'services/agenda';
import { getTodayCallsByDoctor } from 'services/chamadas';
import useSWR from 'swr';
import { getMedicosAgenda, medicos } from 'utils/agenda';
import { isInfrastructureError } from 'utils/apiErrors';
import { formatDateISO } from 'utils/helpers';
import CallHistoryModal from '../painel/HistoricoDeChamadas';
import AgendaHistoryModal from './AgendaHistoryModal';
import AgendasTable from './AgendasTable';
import AgendaToolbar from './AgendaToolbar';
import Encaixe from './Encaixe';
import FormAgenda from './FormAgenda';
import Sidebar from './Sidebar';

function parseAgendaDate(value) {
  if (!value) return null;
  const parsed = parseISO(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function resolveCodMed({ codMedFromUrl, func, doctor }) {
  const medicosPermitidos = getMedicosAgenda({ isFunc: Boolean(func) });

  if (codMedFromUrl) {
    const podeSelecionar = medicosPermitidos.some(
      (medico) => String(medico.codMed) === String(codMedFromUrl)
    );
    if (podeSelecionar) return Number(codMedFromUrl);
  }

  if (doctor?.codMed) return Number(doctor.codMed);
  if (medicosPermitidos.length > 0) return Number(medicosPermitidos[0].codMed);
  return null;
}

function buildAgendaSearch({ codMed, data }) {
  const params = new URLSearchParams();
  if (codMed) params.set('codMed', String(codMed));
  if (data instanceof Date && !Number.isNaN(data.getTime())) {
    params.set('data', formatDateISO(data));
  }
  return params.toString();
}

export default function AgendaMedica() {
  const history = useHistory();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const dataFromUrl = urlParams.get('data');
  const codMedFromUrl = urlParams.get('codMed');
  const func = useSelector(selectFunc);
  const doctor = useSelector(selectDoctor);
  const [showSidebar, setShowSidebar] = useState(false);
  const [codAgendaSelecionada, setCodAgendaSelecionada] = useState(null);
  const [showInlineAgendaForm, setShowInlineAgendaForm] = useState(() => Boolean(func));
  const [agendaContext, setAgendaContext] = useState(null);
  const [verCanceladas, setVerCanceladas] = useState(false);

  const dataDaUrl = parseAgendaDate(dataFromUrl);
  const dataFormatada = dataDaUrl ? formatDateISO(dataDaUrl) : formatDateISO(new Date());
  const dataSelecionada = dataDaUrl || parseAgendaDate(dataFormatada);

  const codMedResolvido = useMemo(
    () => resolveCodMed({ codMedFromUrl, func, doctor }),
    [codMedFromUrl, func, doctor]
  );
  const medicoSelecionado = codMedResolvido != null ? { codMed: codMedResolvido } : null;

  const commitAgendaSearch = useCallback(
    ({ codMed, data }) => {
      const newSearch = buildAgendaSearch({ codMed, data });
      const current = location.search.replace(/^\?/, '');
      if (current === newSearch) return;
      history.replace({ search: newSearch ? `?${newSearch}` : '' });
    },
    [history, location.search]
  );

  const setMedicoSelecionado = useCallback(
    (medico) => {
      commitAgendaSearch({
        codMed: medico?.codMed,
        data: parseAgendaDate(dataFormatada),
      });
    },
    [commitAgendaSearch, dataFormatada]
  );

  const setDataSelecionada = useCallback(
    (data) => {
      commitAgendaSearch({
        codMed: medicoSelecionado?.codMed,
        data,
      });
    },
    [commitAgendaSearch, medicoSelecionado]
  );

  useEffect(() => {
    if (!func && doctor) {
      setShowInlineAgendaForm(false);
    }
  }, [func, doctor]);

  useEffect(() => {
    const dataValida = Boolean(dataDaUrl);
    const medicosPermitidos = getMedicosAgenda({ isFunc: Boolean(func) });
    const codMedUrlValido =
      Boolean(codMedFromUrl) &&
      medicosPermitidos.some((medico) => String(medico.codMed) === String(codMedFromUrl));

    if (dataValida && codMedUrlValido) return;
    if (!codMedResolvido && dataValida) return;

    commitAgendaSearch({
      codMed: codMedResolvido,
      data: parseAgendaDate(dataFormatada),
    });
  }, [dataDaUrl, dataFormatada, codMedFromUrl, codMedResolvido, func, commitAgendaSearch]);

  const {
    data: agendas = [],
    isLoading,
    isValidating,
    error,
    mutate,
  } = useSWR(
    medicoSelecionado?.codMed && dataFormatada
      ? ['/medico/agendas', medicoSelecionado.codMed, dataFormatada, verCanceladas]
      : null,
    () =>
      buscaAgendaDoMedico({
        codMed: medicoSelecionado.codMed,
        dtAgenda: dataFormatada,
        verCanceladas,
      }),
    {
      refreshInterval: 30000,
      revalidateOnFocus: true,
      dedupingInterval: 5000,
      shouldRetryOnError: true,
      errorRetryCount: 2,
      errorRetryInterval: 3000,
    }
  );

  const formAgendaHook = useFormAgenda({
    show: showInlineAgendaForm,
    agendaContext,
    medicoSelecionado,
    dataSelecionada,
    agendasDoDia: agendas,
    onAgendaCreated: mutate,
    onHide: () => setShowInlineAgendaForm(false),
  });

  const handleSaveAgenda = formAgendaHook.handleSubmit(formAgendaHook.onSubmit);
  const isSaveDisabled =
    !showInlineAgendaForm ||
    !agendaContext ||
    formAgendaHook.isSubmitting ||
    formAgendaHook.isLoadingCatalogos ||
    formAgendaHook.hasCatalogosError;

  const {
    handleGerarHorarios,
    handleEncaixe,
    showModalEncaixe,
    setShowModalEncaixe,
    handleConfirmarEncaixe,
    horarioSugestaoEncaixe,
  } = useAgendaActions({
    medicoSelecionado,
    dataSelecionada,
    agendaContext,
    onSuccess: mutate,
  });

  // Instância do Hook do Painel passando o mutate do SWR para revalidação automática
  const {
    callStates,
    setCallStates,
    handleCallPatient,
    handleRecallPatient,
    handleCancelPatient,
    handleArrivePatient,
    handleDonePatient,
    handleViewCallHistory,
    handleViewAgendaHistory,
    handleCloseHistoryModal,
    historyState,
    agendaHistoryState,
    handleCloseAgendaHistoryModal,
  } = usePainel({
    medicoSelecionado,
    medicos,
    onMutate: mutate,
  });

  const agendaSelecionada = useMemo(() => {
    if (!codAgendaSelecionada || agendas.length === 0) return null;
    return agendas.find((a) => String(a.CODAGENDA) === String(codAgendaSelecionada)) || null;
  }, [agendas, codAgendaSelecionada]);

  // Sincroniza chamadas em lote com o banco (ao mudar médico/data ou quando a agenda revalida)
  const syncCalls = useCallback(async () => {
    if (!medicoSelecionado?.codMed || !dataFormatada) return;

    try {
      const calls = await getTodayCallsByDoctor(medicoSelecionado.codMed, dataFormatada);
      if (!Array.isArray(calls)) return;

      const updatedStates = {};
      calls.forEach((c) => {
        const codAgenda = String(c.CODAGENDA || c.codAgenda);

        if (codAgenda) {
          updatedStates[codAgenda] = {
            callId: c.ID || c.id,
            status: c.STATUS ? String(c.STATUS).toLowerCase() : 'calling',
            isLoading: false,
            rawCall: c,
          };
        }
      });

      setCallStates(updatedStates);
    } catch (err) {
      console.error('Erro ao sincronizar chamadas do dia:', err);
    }
  }, [medicoSelecionado?.codMed, dataFormatada, setCallStates]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: agendas triggers call state sync after SWR revalidation
  useEffect(() => {
    syncCalls();
  }, [syncCalls, agendas]);

  // Mantém agendaContext atualizado quando o SWR revalida as agendas (ex.: recepção alterou status),
  // sem sobrescrever edições em andamento no FormAgenda
  const isFormDirty = Boolean(formAgendaHook.form?.formState?.isDirty);
  useEffect(() => {
    if (!agendaContext?.CODAGENDA || agendas.length === 0 || isFormDirty) return;

    const agendaAtualizada = agendas.find(
      (a) => String(a.CODAGENDA) === String(agendaContext.CODAGENDA)
    );

    if (agendaAtualizada && agendaAtualizada !== agendaContext) {
      setAgendaContext(agendaAtualizada);
    }
  }, [agendas, agendaContext, isFormDirty]);

  // Seleciona a linha da tabela
  const handleSelectAgenda = useCallback(
    (agenda) => {
      setCodAgendaSelecionada(agenda?.CODAGENDA ?? null);

      if (!func && doctor) {
        setShowInlineAgendaForm(false);
      }
    },
    [func, doctor]
  );

  // Atualiza o contexto da agenda e abre o formulário quando aplicável
  const handleOpenInlineAgenda = useCallback(
    (agenda = null, options = {}) => {
      const { forceShow = false } = options;
      const codMedContexto = agenda?.CODMED;

      if (!medicoSelecionado?.codMed && !codMedContexto) {
        toast.warn('Selecione um médico para criar uma agenda.');
        return;
      }

      if (!medicoSelecionado?.codMed && codMedContexto) {
        setMedicoSelecionado({ codMed: Number(codMedContexto) });
      }

      setAgendaContext(agenda || null);
      if (forceShow || Boolean(func)) {
        setShowInlineAgendaForm(true);
      }
    },
    [medicoSelecionado, setMedicoSelecionado, func]
  );

  const handleOpenHistory = () => {
    const codPac = agendaContext?.CODPAC;
    const codAgenda = agendaContext?.CODAGENDA;
    const pacienteNome = agendaContext?.NOMEPAC || agendaContext?.PACIENTE;

    if (codPac || codAgenda) {
      handleViewAgendaHistory(codPac, codAgenda, pacienteNome);
    }
  };

  const carregandoAgendas = isLoading || (isValidating && agendas.length === 0);
  const exibirErroAgendas = Boolean(error) && !isValidating;
  const erroInfraestrutura = isInfrastructureError(error);
  const temAgendas = agendas.length > 0;

  return (
    <>
      {/* BARRA SUPERIOR PARA DISPOSITIVOS MÓVEIS */}
      <div className="d-md-none bg-light border-bottom p-2 d-flex align-items-center justify-content-between">
        <Button
          variant="outline-primary"
          size="sm"
          className="d-flex align-items-center gap-1"
          onClick={() => setShowSidebar(true)}
        >
          <BsList size={20} />
          <span>Médicos / Data</span>
        </Button>
        {medicoSelecionado && (
          <span className="fw-bold text-truncate ms-2" style={{ maxWidth: '60%' }}>
            {medicos.find((m) => Number(m.codMed) === Number(medicoSelecionado.codMed))?.apelido ||
              'Médico Selecionado'}
          </span>
        )}
      </div>

      {/* OFFCANVAS PARA DISPOSITIVOS MÓVEIS */}
      <Offcanvas show={showSidebar} onHide={() => setShowSidebar(false)} placement="start">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Seleção de Agendas</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Sidebar
            medicoSelecionado={medicoSelecionado}
            setMedicoSelecionado={(m) => {
              setMedicoSelecionado(m);
              setShowSidebar(false);
            }}
            dataSelecionada={dataSelecionada}
            setDataSelecionada={(d) => {
              setDataSelecionada(d);
              setShowSidebar(false);
            }}
            setAgendaContext={setAgendaContext}
          />
        </Offcanvas.Body>
      </Offcanvas>

      <Row className="m-0" style={{ minHeight: '90vh', flexWrap: 'nowrap', position: 'relative' }}>
        {/* SIDEBAR PARA DESKTOP */}
        <Col
          md={2}
          className="d-none d-md-block bg-white p-2 border-end"
          style={{ minWidth: '180px', maxWidth: '220px' }}
        >
          <Sidebar
            medicoSelecionado={medicoSelecionado}
            setMedicoSelecionado={setMedicoSelecionado}
            dataSelecionada={dataSelecionada}
            setDataSelecionada={setDataSelecionada}
            setAgendaContext={setAgendaContext}
          />
        </Col>

        <Col className="p-2" style={{ minWidth: 0, flex: 1 }}>
          {/* FORMULÁRIO DE AGENDA (Renderizado dinamicamente caso showInlineAgendaForm seja verdadeiro) */}
          <div className="d-none d-md-block">
            {/* Barra Superior com as Ações Principais */}
            <AgendaToolbar
              isEditing={Boolean(agendaContext?.CODAGENDA)}
              agendaContext={agendaContext}
              onGerarHorarios={handleGerarHorarios}
              onNovoEncaixe={handleEncaixe}
              onOpenHistory={handleOpenHistory}
              onSaveAgenda={handleSaveAgenda}
              isSubmitting={formAgendaHook.isSubmitting}
              isSaveDisabled={isSaveDisabled}
              showForm={showInlineAgendaForm}
              // onCloseForm={() => setShowInlineAgendaForm(false)}
            />

            {/* Se o FormAgenda aceitar hookData, você pode repassá-lo direto */}
            {showInlineAgendaForm && (
              <FormAgenda
                show={showInlineAgendaForm}
                onHide={() => setShowInlineAgendaForm(false)}
                medicoSelecionado={medicoSelecionado}
                onAgendaCreated={mutate}
                agendaContext={agendaContext}
                dataSelecionada={dataSelecionada}
                agendasDoDia={agendas}
                hookData={formAgendaHook}
              />
            )}
          </div>

          <Encaixe
            show={showModalEncaixe}
            onHide={() => setShowModalEncaixe(false)}
            onConfirm={handleConfirmarEncaixe}
            horarioSugestao={horarioSugestaoEncaixe}
          />

          <AgendaHistoryModal
            show={agendaHistoryState?.show || false}
            onHide={handleCloseAgendaHistoryModal}
            agendaHistoryEvents={agendaHistoryState?.agendaEvents || []}
            pacienteHistoryEvents={agendaHistoryState?.pacienteEvents || []}
            loading={agendaHistoryState?.loading || false}
            error={agendaHistoryState?.error || ''}
            pacienteNome={agendaHistoryState?.pacienteNome || ''}
            codAgenda={agendaHistoryState?.codAgenda || null}
          />

          <CallHistoryModal
            show={historyState.showModal}
            onHide={handleCloseHistoryModal}
            agenda={historyState.agenda}
            call={historyState.call}
            events={historyState.events}
            loading={historyState.isLoading}
            error={historyState.error}
          />

          {carregandoAgendas ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" />
            </div>
          ) : (
            <>
              {exibirErroAgendas &&
                (erroInfraestrutura ? (
                  <ServiceUnavailableAlert
                    error={error}
                    stale={temAgendas}
                    onRetry={() => mutate()}
                  />
                ) : (
                  <Alert variant="danger" className="text-center mt-2 w-75 mx-auto">
                    <p className="mb-0">Erro ao carregar agendas.</p>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="mt-2"
                      onClick={() => mutate()}
                    >
                      Tentar novamente
                    </Button>
                  </Alert>
                ))}

              {temAgendas ? (
                <AgendasTable
                  agendas={agendas}
                  onSelectAgenda={handleSelectAgenda}
                  agendaSelecionada={agendaSelecionada}
                  onOpenAgenda={handleOpenInlineAgenda}
                  verCanceladas={verCanceladas}
                  onToggleCanceladas={() => setVerCanceladas((prev) => !prev)} // <- Repassando a alternância
                  callStates={callStates}
                  onCallPatient={handleCallPatient}
                  onRecallPatient={handleRecallPatient}
                  onCancelPatient={handleCancelPatient}
                  onArrivePatient={handleArrivePatient}
                  onDonePatient={handleDonePatient}
                  onViewCallHistory={handleViewCallHistory}
                />
              ) : !exibirErroAgendas ? (
                <div className="text-center py-2">Nenhuma consulta nesta data.</div>
              ) : null}
            </>
          )}
        </Col>
      </Row>
    </>
  );
}
